import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import SaveIcon from '@mui/icons-material/Save';
import {
  Button,
  Container,
  Divider,
  Modal,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/en-gb';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { LoadStatus } from '../../constants/constants';
import {
  ActivityStatus,
  ActivityType,
  saveActivity,
  selectActivityLoadStatus,
  selectSelectedActivity,
} from '../../redux/reducers/activityReducer';
import { selectAssessmentFrameworkId, selectAssessmentTitle } from '../../redux/reducers/assessmentReducer';
import { selectTitle } from '../../redux/reducers/catalogsReducer';
import { selectFramework, selectFrameworkTitle } from '../../redux/reducers/frameworkReducer';
import LoadingIndicator from '../LoadingIndicator';
import PersonSelector from '../PersonSelector';
import AddContactModal from '../organization/AddContactModal';

dayjs.extend(advancedFormat);

function validate(details) {
  let validated = true;
  if (details.title === '') {
    validated = false;
  }
  if (details.frameworkId === '') {
    validated = false;
  }
  return validated;
}

function CorrectiveActionModal({ handleClose, finding }) {
  const frameworkId = useSelector((state) => selectAssessmentFrameworkId(state, finding.assessmentId));
  const assessmentTitle = useSelector((state) => selectAssessmentTitle(state, finding.assessmentId));
  const controlTitle = useSelector((state) => selectTitle(state, finding.controlId));
  const frameworkTitle = useSelector((state) => selectFrameworkTitle(state, frameworkId));

  const initialActivityDetails = {
    created: dayjs(Date.Date).format('X'),
    controlIds: [finding.controlId],
    frameworkId,
    findingId: finding.id,
    description: '',
    activityOwner: [],
    autoResolve: false,
    title: '',
    notes: '',
    activityType: ActivityType.CORRECTIVE_ACTION,
    activityStatus: ActivityStatus.NOT_STARTED,
    repeatEvery: '',
    recurringActivity: false,
    deadline: dayjs(Date.Date).format('X'), // Current date in UNIX format
  };
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const activity = useSelector(selectSelectedActivity);
  const activityLoadStatus = useSelector(selectActivityLoadStatus);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [activityDetails, setActivityDetails] = useState(initialActivityDetails);
  const [validationError, setValidationError] = useState(false);
  const [showPersonModal, setShowPersonModal] = useState(false);

  const handleInitialize = () => {
    if (activity.id) {
      setActivityDetails({ ...activity });
    } else {
      setActivityDetails({ ...initialActivityDetails });
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    const newDetails = { ...activityDetails };
    newDetails[name] = value;
    setActivityDetails(newDetails);
    setUnsavedChanges(true);
  };

  const handleSave = () => {
    if (validate(activityDetails)) {
      dispatch(saveActivity(activityDetails));
      handleClose();
    } else {
      setValidationError(true);
    }
  };

  const handleChangeDeadline = (event) => {
    setActivityDetails({ ...activityDetails, deadline: dayjs(event).format('X').toString() });
    setUnsavedChanges(true);
  };

  const handlePersonChange = (idList) => {
    setActivityDetails({ ...activityDetails, activityOwner: idList });
    setUnsavedChanges(true);
  };

  const handleError = (name) => activityDetails[name] === '';

  const handleUnsavedChanges = (_, reason) => {
    if (unsavedChanges) {
      switch (reason) {
        case 'backdropClick':
          // Prevent outside click modal close
          break;
        default:
          setUnsavedChanges(false);
          handleInitialize();
          handleSave();
          break;
      }
    } else {
      handleInitialize();
      handleClose();
    }
  };

  const handleCancel = () => {
    handleInitialize();
    handleClose();
  };

  useEffect(() => {
    if (activity.id) {
      dispatch(selectFramework(activity.frameworkId));
      setActivityDetails({ ...activity });
    } else {
      setActivityDetails({ ...initialActivityDetails });
    }
  }, [activity]);

  return (
    <>
      {showPersonModal && (
        <AddContactModal handleClose={() => setShowPersonModal(false)} />
      )}
      <Modal open onClose={handleUnsavedChanges}>
        {activityLoadStatus === LoadStatus.SUCCESS ? (
          <Container variant="modal" disableGutters>
            <Stack variant="modalTop" spacing={2}>
              <Typography variant="h6">
                {activity.id ? t('activity.modal.editCorrectiveActivity') : t('activity.modal.newCorrectiveActivity')}
              </Typography>
              <TextField
                variant="outlined"
                subtype="signinField"
                onChange={handleChange}
                name="title"
                value={activityDetails.title}
                InputLabelProps={{
                  shrink: true,
                }}
                label={t('activity.modal.title')}
                required
                error={validationError && handleError('title')}
                helperText={validationError && handleError('title') &&
                  (t('error.emptyField'))}
              />
              <TextField
                variant="outlined"
                subtype="signinField"
                onChange={handleChange}
                name="description"
                value={activityDetails.description}
                InputLabelProps={{
                  shrink: true,
                }}
                label={t('activity.modal.description')}
                required
                multiline
                minRows={4}
              />
              <Divider />
              <Stack>
                <Typography variant="overline">{t('activity.modal.correctedFinding')}</Typography>
                <Typography variant="body1">{finding.title}</Typography>
                <Typography variant="body2">{`${controlTitle} - ${assessmentTitle}`}</Typography>
                <Typography variant="body2">{`${frameworkTitle}`}</Typography>
              </Stack>
              <Divider />
              <PersonSelector
                title={t('activity.modal.activityOwner')}
                handlePersonChange={handlePersonChange}
                handleAddNew={() => setShowPersonModal(true)}
                contacts={activityDetails.activityOwner}
                fill
              />
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
                <DatePicker
                  label={t('activity.modal.deadline')}
                  format="DD.MM.YYYY"
                  value={dayjs.unix(activityDetails.deadline)}
                  onChange={handleChangeDeadline}
                />
              </LocalizationProvider>
            </Stack>
            <Stack variant="modalButtons" direction="row" justifyContent="flex-end" spacing={1}>
              <Button variant="text" startIcon={<ClearIcon />} onClick={() => handleCancel()}>
                {t('activity.modal.cancel')}
              </Button>
              <Button
                disabled={!activityDetails.title || !activityDetails.description}
                variant="contained"
                startIcon={activity.id ? <SaveIcon /> : <AddIcon />}
                onClick={() => handleSave()}
              >
                {activity.id ? t('activity.modal.saveChanges') : t('activity.modal.newActivity')}
              </Button>
            </Stack>
          </Container>
        ) : (
          <LoadingIndicator />
        )}
      </Modal>
    </>
  );
}

export default CorrectiveActionModal;
